import Link from 'next/link';
import { Home, ChevronRight } from 'lucide-react';

/**
 * PageHero — shared inner-page hero component
 *
 * Layout approach (fixes text cutoff on all devices):
 *   - <section> is position:relative, min-h responsive, NO overflow:hidden on the section itself
 *   - <img> is position:absolute, z-index:0, inset-0, object-cover — acts as pure background
 *   - gradient overlay is position:absolute, z-index:1
 *   - text content is position:relative, z-index:2 — always renders fully, never clipped
 *   - section height is driven by content (min-h), not a fixed h-80 that clips
 *
 * Props:
 *   image      — Unsplash URL string
 *   imageAlt   — alt text
 *   breadcrumb — array of { label, href } — last item has no href (current page)
 *   badge      — optional JSX for the pill badge above the heading
 *   title      — string or JSX  (required)
 *   subtitle   — string or JSX (optional)
 *   align      — 'center' | 'left' (default: 'center')
 *   minHeight  — Tailwind class string (default: 'min-h-[300px] md:min-h-[420px]')
 */
export default function PageHero({
  image,
  imageAlt = 'Page hero',
  breadcrumb = [],
  badge,
  title,
  subtitle,
  align = 'center',
  minHeight = 'min-h-[300px] md:min-h-[420px]',
}) {
  const isCenter = align === 'center';

  return (
    <section className={`page-hero relative w-full ${minHeight} flex items-center`}>

      {/* ── BG image — z-0, absolute, never clips text ── */}
      {image && (
        <img
          src={image}
          alt={imageAlt}
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ zIndex: 0 }}
          loading="eager"
          fetchPriority="high"
        />
      )}
      {!image && (
        <div
          className="absolute inset-0 bg-gradient-to-br from-slate-900 via-sky-950 to-slate-900"
          style={{ zIndex: 0 }}
        />
      )}

      {/* ── Gradient overlay — z-1 ── */}
      <div
        className="absolute inset-0"
        style={{
          zIndex: 1,
          background: isCenter
            ? 'linear-gradient(to bottom, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.45) 40%, rgba(15,23,42,0.80) 100%)'
            : 'linear-gradient(to top, rgba(15,23,42,0.92) 0%, rgba(15,23,42,0.65) 50%, rgba(15,23,42,0.30) 100%)',
        }}
      />

      {/* ── Content — z-2, relative, full padding so it's never hidden ── */}
      <div
        className={`relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 ${isCenter ? 'text-center flex flex-col items-center' : ''}`}
        style={{ zIndex: 2 }}
      >

        {/* Breadcrumb */}
        {breadcrumb.length > 0 && (
          <nav className="flex items-center gap-1.5 text-sm mb-5 flex-wrap" aria-label="Breadcrumb" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
            <Link href="/" className="flex items-center gap-1 text-slate-200 hover:text-sky-300 transition-colors">
              <Home size={13} /> Inicio
            </Link>
            {breadcrumb.map((crumb, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <ChevronRight size={13} className="text-slate-400" />
                {crumb.href ? (
                  <Link href={crumb.href} className="text-slate-200 hover:text-sky-300 transition-colors">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-sky-300 font-medium">{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}

        {/* Badge */}
        {badge && (
          <div className="mb-5">
            {badge}
          </div>
        )}

        {/* Title */}
        <h1 className={`font-display font-bold text-white leading-tight tracking-tight
          text-3xl sm:text-4xl md:text-5xl lg:text-6xl
          ${subtitle ? 'mb-4' : 'mb-0'}`}>
          {title}
        </h1>

        {/* Subtitle */}
        {subtitle && (
          <p className="text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl">
            {subtitle}
          </p>
        )}
      </div>
    </section>
  );
}
